import i18n, { getAppLanguage } from './i18n';
import { getCompanyRoleTranslationKey } from 'org-graph/localization';

const detailLabelAliases: Record<string, string> = {
  'prediction delta': 'absolute_increase',
  'change type': 'change_type',
  'changed fields': 'changed_fields',
  'observed companies': 'current_company_count',
  'companies with kfm debt': 'current_matching_company_count',
  'share with kfm debt': 'current_ratio',
  'has active kfm debt': 'has_active_kfm_debt',
  'bankruptcy ended': 'konkurs_avslutad',
  'bankruptcy started': 'konkurs_inledd',
  'liquidation ended': 'likvidation_avslutad',
  'liquidation decided': 'likvidation_beslutad',
  'matching companies': 'matching_external_ids',
  'new payment orders': 'payment_order_entries',
  'new payment order dates': 'payment_order_dates',
  'relative increase': 'relative_increase',
  'predicted default risk': 'scorecard_prediction',
  'scorecard rating class': 'scorecard_rating_class',
  threshold: 'threshold',
  'current kfm debt amount': 'total_skuldsaldo_current',
  rank: 'rank',
  role: 'funktionar_funk_text',
};

function normalize(value: string) {
  return value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

function humanizeBackendKey(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function getMonitoringRuleName(ruleKey: string, fallback?: string) {
  const key = `monitoring.rules.${ruleKey}.name`;
  return getAppLanguage() === 'sv' && i18n.exists(key, { lng: 'sv' })
    ? i18n.t(key, { lng: 'sv' })
    : fallback?.trim() || ruleKey;
}

export function getMonitoringRuleDescription(ruleKey: string, fallback?: string) {
  const key = `monitoring.rules.${ruleKey}.description`;
  return getAppLanguage() === 'sv' && i18n.exists(key, { lng: 'sv' })
    ? i18n.t(key, { lng: 'sv' })
    : fallback?.trim() || '';
}

export function getMonitoringDetailLabel(label: string) {
  const normalized = normalize(label);
  const semanticKey = detailLabelAliases[normalized] ?? normalized.replace(/ /g, '_');
  const key = `monitoring.detailLabels.${semanticKey}`;
  return i18n.exists(key) ? i18n.t(key) : label;
}

export function getMonitoringDetailSemanticKey(label: string) {
  const normalized = normalize(label);
  return detailLabelAliases[normalized] ?? normalized.replace(/ /g, '_');
}

export function getMonitoringChangeType(value: string) {
  const normalized = normalize(value);
  const aliases: Record<string, string> = {
    added: 'added', add: 'added',
    removed: 'removed', remove: 'removed',
    changed: 'changed', change: 'changed',
  };
  const semanticKey = aliases[normalized];
  return semanticKey ? i18n.t(`monitoring.changeTypes.${semanticKey}`) : value;
}

export function getMonitoringPropertyLabel(propertyName: string) {
  const key = `monitoring.propertyLabels.${propertyName}`;
  return i18n.exists(key) ? i18n.t(key) : humanizeBackendKey(propertyName);
}

export function getMonitoringPropertyValue(propertyName: string, value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const key = `monitoring.propertyValues.${propertyName}.${value}`;
  return i18n.exists(key) ? i18n.t(key) : null;
}

export function getMonitoringSnapshotStatus(status: string) {
  const key = `monitoring.snapshotStatuses.${status}`;
  return i18n.exists(key) ? i18n.t(key) : humanizeBackendKey(status);
}

export function getCompanyStatus(value: string) {
  const key = `assessment.details.statusValues.${value}`;
  return i18n.exists(key) ? i18n.t(key) : value;
}

export function getCompanyRole(value: string) {
  const key = getCompanyRoleTranslationKey(value);
  return key && i18n.exists(key) ? i18n.t(key) : value;
}

export function getRuleMetadataName(id: string, fallback: string) {
  const key = `rules.metadata.${id}`;
  return getAppLanguage() === 'sv' && i18n.exists(key, { lng: 'sv' })
    ? i18n.t(key, { lng: 'sv' })
    : fallback;
}

const customRelationshipTypeAliases: Record<string, string> = {
  bankaccount: 'bankAccount',
  bank_account: 'bankAccount',
  email: 'email',
  phone: 'phone',
  phonenumber: 'phone',
  phone_number: 'phone',
  carregnumber: 'carRegNumber',
  car_reg_number: 'carRegNumber',
  case: 'case',
};

export function getCustomRelationshipType(value: string) {
  const normalized = value.trim().replace(/[ -]/g, '_').toLowerCase();
  const semanticKey = customRelationshipTypeAliases[normalized.replace(/_/g, '')]
    ?? customRelationshipTypeAliases[normalized];
  const key = semanticKey ? `customLinking.types.${semanticKey}` : '';
  return key && i18n.exists(key) ? i18n.t(key) : value;
}

export function getCustomRelationshipSourceType(value: string) {
  const normalized = value.trim().toLowerCase();
  const key = normalized === 'company' || normalized === 'person'
    ? `customLinking.types.${normalized}`
    : '';
  return key && i18n.exists(key) ? i18n.t(key) : value;
}
